import React, { useEffect, useState } from 'react';
import { Alert, Button, Form } from 'react-bootstrap';
import axios from '../../../../services/AxiosInstance';
import { getApiUrl } from '../../../../utils/ApiUtils';

// Period per proposed line (periodisering spec D5/D6). A `strong` line comes
// from confirmed invoice dates or a found extraction; anything weaker is a
// prefill the user confirms by editing a month, pressing "Bekräfta perioden"
// or choosing a candidate item. The answer is `incomplete` until every
// periodised line has both months and, when weak, that confirmation.
const SOURCE_LABELS = { confirmed: 'Bekräftad', analyzer_field: 'Hittad i fakturan', text_pattern: 'Hittad i texten' };
const MONTHS = ['januari','februari','mars','april','maj','juni','juli','augusti','september','oktober','november','december'];

export function sourceLabel(line) {
  if (line.source === 'history') return line.label || 'Förra året';
  return SOURCE_LABELS[line.source] || '';
}

export function monthLabel(period) {
  if (!period || !/^\d{4}-\d{2}$/.test(period)) return period || '';
  return `${MONTHS[Number(period.slice(5)) - 1]} ${period.slice(0, 4)}`;
}

export function monthCount(start, end) {
  if (!start || !end || !/^\d{4}-\d{2}$/.test(start) || !/^\d{4}-\d{2}$/.test(end)) return 0;
  return (Number(end.slice(0, 4)) - Number(start.slice(0, 4))) * 12 + Number(end.slice(5)) - Number(start.slice(5)) + 1;
}

/** The answer the modal sends, with `incomplete` while a line still needs the user. */
export function periodisationAnswer(lines, excludedLineIndex, confirmed) {
  const answer = lines.map((l) => (l.lineIndex === excludedLineIndex ? { ...l, choice: 'expense', declineReason: 'not_applicable' } : l));
  const incomplete = answer.some((l) => l.choice === 'periodise' && l.lineIndex !== excludedLineIndex
    && (monthCount(l.periodStart, l.periodEnd) <= 0 || (!l.strong && !confirmed.has(l.lineIndex))));
  return { lines: answer, incomplete };
}

/**
 * What a consumer sends and whether it may confirm yet: an `incomplete` answer
 * blocks, a complete one is sent as `{ lines }` only. The UI flag never reaches
 * the backend (the expense booking schema rejects unknown keys).
 */
export function periodisationAnswerForConfirm(answer) {
  if (!answer) return { blocked: false, asPeriodisation: null };
  if (answer.incomplete) return { blocked: true, asPeriodisation: null };
  return { blocked: false, asPeriodisation: { lines: answer.lines } };
}

export default function PeriodisationNudge({ companyId, sourceType, sourceId, date, lines, description, counterpartyId,
  servicePeriodStart, servicePeriodEnd, payment, yearEnd, newBooking, excludedLineIndex, onChange }) {
  const [suggestion, setSuggestion] = useState(null);
  const [choices, setChoices] = useState([]);
  const [confirmed, setConfirmed] = useState(new Set());
  const [error, setError] = useState('');
  const input = JSON.stringify({ sourceType, sourceId, date, lines, description, counterpartyId, servicePeriodStart, servicePeriodEnd, payment, yearEnd, newBooking });
  useEffect(() => {
    let live = true;
    setSuggestion(null); setChoices([]); setConfirmed(new Set()); onChange(null);
    if (!sourceId || !date || (sourceType !== 'customer_invoice' && !lines?.length)) return undefined;
    const timer = setTimeout(() => axios.post(getApiUrl(`${companyId}/periodisering/detection/evaluate`), JSON.parse(input), { withCredentials: true })
      .then(({ data }) => { if (live) { setError(''); setSuggestion(data); setChoices(data?.prefill?.lines || []); } })
      .catch((e) => { if (live && e.response?.status !== 403) setError('Förslaget kunde inte hämtas. Försök igen innan du bokför.'); }), 300);
    return () => { live = false; clearTimeout(timer); };
    // The serialized input keeps previews from reloading on each parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId, input]);
  useEffect(() => {
    if (suggestion?.suggestion === 'none' || !suggestion || suggestion.status !== 'open') { onChange(null); return; }
    onChange(periodisationAnswer(choices, excludedLineIndex, confirmed));
  }, [suggestion, choices, confirmed, excludedLineIndex, onChange]);
  if (error) return <Alert variant="warning">{error}</Alert>;
  if (!suggestion || suggestion.suggestion === 'none' || suggestion.status !== 'open') return null;
  const confirm = (lineIndex) => setConfirmed((old) => new Set([...old, lineIndex]));
  const change = (i, patch) => setChoices((old) => old.map((l, j) => (i === j ? { ...l, ...patch } : l)));
  const changePeriod = (i, l, patch) => { change(i, patch); confirm(l.lineIndex); };
  const revenue = sourceType === 'customer_invoice';
  return <div className="modern-card p-3 mb-3">
    <h5>{revenue ? 'Gäller fakturan en period?' : 'Det här ser ut som en kostnad som gäller flera månader'}</h5>
    <p>{suggestion.reason}</p>
    <a href={`/${companyId}/ai-assistant`} target="_blank" rel="noopener noreferrer">Fråga Finn</a>
    {choices.map((l, i) => {
      const excluded = l.lineIndex === excludedLineIndex;
      const recurring = suggestion.prefill.threshold.regime === 'k2' && !revenue && l.remembered?.choice !== 'periodise';
      const count = monthCount(l.periodStart, l.periodEnd);
      const label = sourceLabel(l);
      const needsConfirmation = !l.strong && !confirmed.has(l.lineIndex);
      return <fieldset key={i} className="mb-3" disabled={excluded}>
        <legend className="h6">Konto {l.plAccountNumber} · {Number(l.totalAmount).toLocaleString('sv-SE')} kr</legend>
        {excluded && <p>Raden bokförs som tillgång.</p>}
        {l.remembered && <p>Förra gången valde du {l.remembered.choice === 'periodise' ? 'att periodisera' : 'att kostnadsföra direkt'}.</p>}
        <Form.Control as="select" aria-label="Periodisera raden" value={excluded ? 'expense' : l.choice} onChange={(e) => change(i, { choice: e.target.value })}>
          <option value="periodise">Periodisera</option><option value="expense">{revenue ? 'Nej, intäktsför nu' : 'Nej, kostnadsför nu'}</option>
        </Form.Control>
        {l.choice === 'periodise' && !excluded ? <>
          <Form.Label>Vad gäller beloppet?</Form.Label><Form.Control value={l.name} maxLength={200} onChange={(e) => change(i, { name: e.target.value })} />
          <div className="d-flex align-items-center flex-wrap gap-2">
            <label>Från <input type="month" value={l.periodStart || ''} onChange={(e) => changePeriod(i, l, { periodStart: e.target.value })} /></label>
            <label>Till <input type="month" value={l.periodEnd || ''} onChange={(e) => changePeriod(i, l, { periodEnd: e.target.value })} /></label>
            {count > 0 && label ? <span className="text-muted">{label}</span> : null}
            {count <= 0 && !l.candidates?.length ? <span className="text-muted">Ange period</span> : null}
          </div>
          {l.evidence && <p className="text-muted mb-1"><small>”{l.evidence}”</small></p>}
          {l.candidates?.length ? <div className="mb-2">
            <span className="text-muted">Ange period</span> — fakturan anger flera perioder, välj den som gäller raden:
            <ul className="mb-1">{l.candidates.map((c, k) => <li key={k}>
              {c.description || 'Rad'} · {Number(c.amount).toLocaleString('sv-SE')} kr · {monthLabel(c.periodStart)} – {monthLabel(c.periodEnd)}{' '}
              <Button size="sm" variant="link" onClick={() => changePeriod(i, l, { periodStart: c.periodStart, periodEnd: c.periodEnd })}>Använd</Button>
            </li>)}</ul>
          </div> : null}
          {count > 0 && needsConfirmation && <Button size="sm" variant="outline-primary" className="mb-2" onClick={() => confirm(l.lineIndex)}>Bekräfta perioden</Button>}
          <Form.Label>Balanskonto</Form.Label><Form.Control value={l.balanceAccountNumber} onChange={(e) => change(i, { balanceAccountNumber: e.target.value })} />
          {count > 0 && <p>{(l.totalAmount / count).toLocaleString('sv-SE', { maximumFractionDigits: 2 })} kr bokförs som {revenue ? 'intäkt' : 'kostnad'} varje månad till och med {monthLabel(l.periodEnd)}.</p>}
        </> : <Form.Control as="select" aria-label="Orsak" value={l.declineReason || 'expense_now'} onChange={(e) => change(i, { declineReason: e.target.value })}>
          <option value="expense_now">Redovisa direkt</option>{recurring && <option value="recurring_7_9">Återkommer varje år, varierar högst 20 %</option>}<option value="not_applicable">Gäller inte flera månader</option>
        </Form.Control>}
      </fieldset>;
    })}
  </div>;
}
